import { CategoryNavigationMenu } from '@diritto/common/navigation/constant';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import {
  ContentListType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { useTrackingState } from 'contexts/TrackingProvider';
import { ContentItemMeta } from 'app-components/contents/ContentItemMeta';
import React, { useCallback, useMemo } from 'react';
import { Link } from 'ui-components/common/Link';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { IconCountType } from 'ui-components/contents/IconCount';
import { getCategoryNavMenuFromCategoryType, getCategoryTypeFromId } from 'utils/category';
import { getIconCountMap } from 'utils/content';

interface LatestContentItemProps {
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  selectedCategoryId?: number;
  listItemIndex?: number;
  listType: ContentListType;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.Latest,
};

export const LatestContentItem = ({
  content,
  profile,
  selectedCategoryId,
  listItemIndex,
  listType,
}: LatestContentItemProps) => {
  const { tracker } = useTrackingState();

  const iconCountMap = useMemo(
    () =>
      getIconCountMap([IconCountType.View, IconCountType.Recommend, IconCountType.Favorite], {
        viewCount: content.counts.viewCount,
        recommendCount: content.counts.recommendCount,
        favoriteCount: content.counts.favoriteCount,
      }),
    [content],
  );

  const handleClick = useCallback(() => {
    const categoryType = getCategoryTypeFromId(selectedCategoryId as number);
    const categoryNavMenu = getCategoryNavMenuFromCategoryType(categoryType);

    tracker.track(TrackingEventType.SelectContentListItem, {
      listType,
      listItemIndex: listItemIndex as number,
      contentId: content.id,
      params: { category: categoryNavMenu || CategoryNavigationMenu.All },
      trackerExtraInfo,
    });
  }, [selectedCategoryId, tracker, listType, listItemIndex, content.id]);

  return (
    <StyledContainer onClick={handleClick}>
      <Link href={`/contents/${content.id}`}>
        <ContentCover content={content} />
      </Link>
      <StyledMetaContainer>
        <ContentItemMeta
          content={content}
          profile={profile}
          iconCounts={iconCountMap}
          showCategoryType
          showKeyword
          showEpisodeCount
          showDescription
          maxShowKeywordCount={3}
          maxTitleLine={1}
          href={`/contents/${content.id}`}
        />
      </StyledMetaContainer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

const StyledMetaContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
  gap: 4px;
`;
